import type { Device } from 'src/types/Device';
import type { ChartConfig } from 'src/types/Chart';
import { getAddonStatus } from '../addons/addon_helpers';
import { isPeripheralDevice } from '../addons/addon_device_helpers';
import { get } from 'svelte/store';
import { devices } from 'src/stores/lists/devices';
import { getLedStripComponent, isPill } from '../interface_helpers';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import { compareFWDates } from '../firmware_compare_helpers';
import { isTestUser } from '../user_helpers';

/**
 * The Pill status configurator.
 * @param device The device to get the status for.
 * @returns Interface status array.
 */
export const getPillDeviceStatus = (device: Device) => {
	const interface_data = getAddonStatus(device, [], true, true) || ([] as any[]);
	const hasSwitch = interface_data.some((i) => i.peripheralType === 'switch');

	const firstSensor = interface_data.findIndex((i) => i.peripheralType !== 'switch');

	// First sensor is main if no outputs present
	// First available sensor is always main in the drawer
	if (firstSensor !== -1) {
		interface_data[firstSensor].drawer_main = true;
		if (!hasSwitch) {
			interface_data[firstSensor].main = true;
			interface_data[firstSensor].big_val = true;
		}
	}

	return interface_data;
};

/**
 * Get the event log tags for The Pill device.
 * @param device The device to get the event log tags for.
 * @returns An array of event log tags.
 */
export const getThePillEventLogTags = (device) => {
	const realDeviceID = device.id.split('_')[0];
	//@ToDo: Do not hardcode the channels
	let tags = [realDeviceID, realDeviceID + '_200', realDeviceID + '_201', realDeviceID + '_202'];
	return tags;
};

/**
 * Check if the temperature sensor is not detected.
 * @param status The device status
 * @returns True if the temperature sensor is not detected, false otherwise.
 */
export const checkNoTemperature = (status) => {
	const temperature = `${status?.['temperature:200']?.tC || ''}`;
	return !temperature || temperature.includes('N/A');
};

/**
 * Check if mode is digital IO and no inputs/outputs configured.
 * @param status The device status
 * @returns True if no digital input/output data is present, false otherwise.
 */
export const checkNoDigitalIONotConfigured = (settings) => {
	const pillSettings = settings?.pill;
	if (
		pillSettings?.mode === 'digital_io' &&
		pillSettings?.pin0_mode === 'none' &&
		pillSettings?.pin1_mode === 'none' &&
		pillSettings?.pin2_mode === 'none'
	) {
		return true;
	}
	return false;
};

/**
 * Checks if the device is extracted Pill peripheral device.
 * @param device The device to check
 * @returns
 */
export const isPillPeripheralDevice = (device: Device): boolean => {
	if (!isPeripheralDevice(device?.type)) {
		return false;
	}
	return getMainDeviceOfPillPeripheral(device) !== undefined;
};

/** Get the main device of a Pill peripheral device.
 * @param peripheral The peripheral device.
 * @returns The main Pill device or undefined if not found.
 */
export const getMainDeviceOfPillPeripheral = (peripheral: Device): Device | undefined => {
	const parentID = peripheral.id.split('_')[0];
	const parentDevice = get(devices)?.[parentID];
	return isPill(parentDevice?.type) ? parentDevice : undefined;
};

/**
 * Check if Pill has UART support.
 * Support comes after dev FW from 20251210.
 * @param settings The device settings.
 * @returns True if the device has UART support, false otherwise.
 */
export const hasUARTSupport = (settings: DeviceSettingsG2): boolean => {
	return compareFWDates(settings?.sys?.device?.fw_id, '>=', '20251205');
};

/**
 * Check if Pill has LED Strip support.
 * Support comes after dev FW;
 * @TODO: Update when released.
 * @param settings The device settings.
 * @returns True if the device has LED support, false otherwise.
 */
export const hasLEDStripSupport = (settings: DeviceSettingsG2): boolean => {
	return isTestUser();
	//@ToDo: Update when dev FW is released or available in main.
	// return compareFWDates(settings?.sys?.device?.fw_id, '>=', '20260416');
};

/**
 * Check if the Pill device is currently in LED Strip mode.
 * @param settings The device settings.
 * @returns True if the device is in LED Strip mode, false otherwise.
 */
export const isPillLedStripMode = (settings: DeviceSettingsG2 & { pill?: any }): boolean => {
	return settings?.pill?.mode === 'ledstrip';
};

/**
 * Check if a Pill mode value represents a serial-communication peripheral
 * (`js_uart`, `mb_client`, or the legacy `uart`).
 */
export const isPillSerialMode = (mode: string | undefined | null): boolean => {
	return mode === 'uart' || mode === 'js_uart' || mode === 'mb_client';
};

/** True when the component path targets a `switch:N` component. */
const isSwitchComponent = (component: string | undefined): boolean =>
	!!component && /^switch:\d+$/.test(component);

/** All `switch:N` component keys present in the device status. */
const getSwitchComponents = (status: any): string[] => {
	if (!status || typeof status !== 'object') return [];
	return Object.keys(status).filter(isSwitchComponent);
};

export type PillSingleToggleTarget =
	| { type: 'ledstrip'; component: string }
	| { type: 'switch'; component: string };

/**
 * Resolve the single on/off target for a Pill from its status: an `ledstrip:N`
 * component, or exactly one `switch:N`. Returns null otherwise.
 */
export const getPillSingleToggleTarget = (status: any): PillSingleToggleTarget | null => {
	const ledStrip = getLedStripComponent(status);
	if (ledStrip) return { type: 'ledstrip', component: ledStrip };

	const switches = getSwitchComponents(status);
	if (switches.length === 1) return { type: 'switch', component: switches[0] };

	return null;
};
/** Check if the device has virtual groups in its settings.
 * @param settings The device settings.
 * @returns True if the device has virtual groups, false otherwise.
 */

export const hasVirtualGroups = (settings: DeviceSettingsG2): boolean => {
	if (!settings || !Object.keys(settings)?.length) {
		return false;
	}

	return Object.keys(settings).filter((key) => new RegExp(`^group:2\\d{2}$`).test(key))?.length > 0;
};

/**
 * Check if a Pill measurement chart is a temperature chart.
 * Used to apply temperature unit override and Fahrenheit conversion
 * only to temperature sensors, not voltmeter or other analog sensors.
 */
export const isPillTemperatureMeasurement = (
	chartConfig: ChartConfig,
	units: string,
	deviceType: string,
): boolean => {
	if (chartConfig?.name !== 'measurement') {
		return false;
	}
	if (!isPill(deviceType)) {
		return false;
	}
	return units === '°C' || units === '°F';
};
